import { SMap, TMap } from '@client/new/helpers/types';
import { FieldErrors } from 'react-hook-form';
import { ISection } from './schemas';
import {
  disabledSectionTypePostfix,
  EmailCaptureSectionSchema,
  SectionSchema,
} from './OTPLandingPage.schema';

const sectionTitlesMap: Partial<
  TMap<ISection['type'] | 'FeaturesNew' | 'OfferDetails', string>
> = {
  OTPFeatures: 'Features',
  FeaturesNew: 'Features',
  TextSection1: 'Headline + text + media',
  TextSection2: 'Headline + text + 2 images',
  ProgramStats: 'Program info',
  EmailCapture: 'Email Capture',
  OfferDetails: 'Offer display',
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toErrors = <T extends SMap<any>>(
  sections: ISection[],
  e: FieldErrors<T>,
) => {
  if (!e.sections || !Array.isArray(e.sections)) return null;

  const messages: { names: string[]; msg: string }[] = [];
  e.sections.forEach((v, i) => {
    if (!v || i >= sections.length) return null;
    const title = toSectionTitle(sections[i]);
    Object.keys(v).forEach((k) => {
      const error = v[k];
      if (!error?.message) return;
      const names = [`#sections.${i}`, `sections.${i}.${k}`];
      const errorMsg = error.message;
      // if (errorMsg === 'Cannot be empty') errorMsg = `${camelCaseToTitle(k)} cannot be empty`;
      const msg = `Section ${title}: ${errorMsg}`;

      if (messages.find((m) => m.msg === msg)) return;
      messages.push({ msg, names });
    });
  });

  if (messages.length === 0) return null;
  return {
    title:
      messages.length > 1 ? 'There are several problems' : 'There is a problem',
    messages,
  };
};

export const toSectionTitle = (
  section: Pick<SectionSchema | ISection, 'type' | 'enabled'>,
) => {
  let title = `${
    sectionTitlesMap[section.type.replace(disabledSectionTypePostfix, '')] ||
    section.type.replace(disabledSectionTypePostfix, '')
  }`;
  if (
    (section.type === 'EmailCapture' ||
      section.type === 'EmailCapture-disabled') &&
    (section as EmailCaptureSectionSchema)?.displayAsPopup
  )
    title = `Popup ${title}`;

  if (section.enabled === false) title = `Disabled - ${title}`;
  return title;
};

export const onEnabledChange = <T extends Pick<SectionSchema, 'type'>>(
  section: T,
  enabled: boolean,
): Partial<SectionSchema> => {
  if (section.type === 'Introduction')
    return { enabled: true, type: section.type };

  const type = enabled
    ? section.type.replace(disabledSectionTypePostfix, '')
    : !section.type.endsWith(disabledSectionTypePostfix)
      ? `${section.type}${disabledSectionTypePostfix}`
      : section.type;

  return { enabled, type } as Partial<SectionSchema>;
};

export const extendSectionsType = (sections: ISection[]) =>
  sections.map((v) => {
    if (v.enabled === false && !v.type.endsWith(disabledSectionTypePostfix))
      return {
        ...v,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        type: `${v.type}${disabledSectionTypePostfix}` as any,
      } as ISection;
    return v;
  });

export const cleanupSectionsType = (sections: ISection[]) =>
  sections.map((value) => {
    if (value.type.endsWith(disabledSectionTypePostfix))
      return {
        ...value,
        type: value.type.replace(disabledSectionTypePostfix, ''),
        enabled: value.type.endsWith(disabledSectionTypePostfix)
          ? false
          : value.enabled,
      } as ISection;
    return value;
  });

// `workoutsPreviewEnabled` is a UI-only flag and isn't persisted, so when a
// config is loaded derive it from the saved workouts: enabled when the
// OfferDetails section already has selected workouts.
export const deriveWorkoutsPreviewEnabled = (sections: ISection[]) =>
  sections.map((section) => {
    if ((section.type as string) !== 'OfferDetails') return section;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const s = section as any;
    return {
      ...s,
      workoutsPreviewEnabled:
        Array.isArray(s.workoutIds) && s.workoutIds.length > 0,
    } as ISection;
  });

// `workoutsPreviewEnabled` is a UI-only flag used for form validation; it must
// not be persisted to the backend config. When the preview is off, the selected
// workouts must not be persisted either, otherwise the flag would be derived
// back as enabled on the next load.
export const stripOfferUiFields = (sections: ISection[]) =>
  sections.map((section) => {
    if ((section.type as string) !== 'OfferDetails') return section;
    const { workoutsPreviewEnabled, ...rest } = section as ISection & {
      workoutsPreviewEnabled?: boolean;
      workoutIds?: number[];
    };
    if (workoutsPreviewEnabled === false)
      return { ...rest, workoutIds: [] } as ISection;
    return rest as ISection;
  });

export const ensureIntroductionDefaults = (
  sections: ISection[],
  fallbackPrimaryColor?: string,
) => {
  const safePrimaryColor =
    fallbackPrimaryColor && fallbackPrimaryColor.trim().length > 0
      ? fallbackPrimaryColor
      : '#000000';

  const normalizePriceBefore = (value: unknown) => {
    if (value === undefined || value === null) return null;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed.length === 0) return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  return sections.map((section) => {
    if (section.type !== 'Introduction') return section;

    return {
      ...section,
      primaryColor: section.primaryColor ?? safePrimaryColor,
      priceBefore: normalizePriceBefore(section.priceBefore),
    };
  });
};
